import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Gift, Copy, Loader2, Users, Banknote, Wallet, Clock, CheckCircle2, ListChecks, Network, Layers, ChevronRight } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const STATUS_META = {
  pending: { label: "Beklemede", color: "#F59E0B", bg: "rgba(245,158,11,0.15)", icon: Clock },
  queued: { label: "Sırada", color: "#60A5FA", bg: "rgba(59,130,246,0.15)", icon: ListChecks },
  completed: { label: "Tamamlandı", color: "#10B981", bg: "rgba(16,185,129,0.15)", icon: CheckCircle2 },
};

const LEVEL_COLORS = ["#10B981", "#3B82F6", "#8B5CF6", "#F59E0B", "#EC4899"];

const flattenTree = (nodes, acc = []) => {
  for (const n of nodes) {
    acc.push(n);
    if (n.children && n.children.length) flattenTree(n.children, acc);
  }
  return acc;
};

export const ReferralPanel = () => {
  const [data, setData] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [tree, setTree] = useState([]);
  const [amount, setAmount] = useState("");
  const [address, setAddress] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [r, w, t] = await Promise.all([
        api.get("/me/referral"), api.get("/me/withdrawals"), api.get("/me/referral/tree"),
      ]);
      setData(r.data);
      setWithdrawals(w.data);
      setTree(t.data.tree || []);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refLink = data?.ref_code ? `${window.location.origin}/register?ref=${data.ref_code}` : "";

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} kopyalandı`);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/me/withdraw", { amount: parseFloat(amount), address: address.trim() });
      toast.success("Çekim talebi oluşturuldu");
      setAmount("");
      setAddress("");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Talep oluşturulamadı");
    } finally {
      setSubmitting(false);
    }
  };

  const balance = data?.referral_balance ?? 0;
  const minW = data?.min_withdraw ?? 10;

  return (
    <div data-testid="referral-panel" className="rounded-2xl border" style={{ background: "var(--bg-card)", borderColor: "var(--border-subtle)" }}>
      <div className="px-5 sm:px-6 py-4 border-b flex items-center gap-2 flex-wrap" style={{ borderColor: "var(--border-subtle)" }}>
        <Gift className="w-4 h-4 text-emerald-400" />
        <h3 className="text-sm font-bold">Referans & Komisyon</h3>
        <span className="text-[11px] font-semibold px-2 py-1 rounded-full" style={{ background: "rgba(16,185,129,0.12)", color: "#10B981" }}>
          %{data?.commission_pct ?? 30} komisyon
        </span>
        <span data-testid="referral-balance" className="ml-auto text-sm font-bold px-3 py-1 rounded-full" style={{ background: "rgba(16,185,129,0.15)", color: "#10B981" }}>
          {loading ? "…" : `${balance} USDT çekilebilir`}
        </span>
      </div>

      <div className="px-5 sm:px-6 py-4 space-y-5">
        {/* referral link */}
        <div>
          <label className="block text-[10px] uppercase tracking-wider mb-1" style={{ color: "var(--text-muted)" }}>Referans Linkiniz</label>
          <div className="flex items-center justify-between gap-2 rounded-lg px-3 py-2" style={{ background: "var(--bg-main)" }}>
            <div data-testid="referral-link" className="text-[12px] font-mono-term truncate" style={{ color: "var(--text-primary)" }}>{refLink || "…"}</div>
            <button data-testid="copy-referral-link" onClick={() => copy(refLink, "Referans linki")} disabled={!refLink}
              className="p-2 rounded-lg border shrink-0" style={{ borderColor: "var(--border-subtle)" }}><Copy className="w-4 h-4" /></button>
          </div>
          <p className="text-[11px] mt-1" style={{ color: "var(--text-muted)" }}>
            Bu linkle kaydolan herkesin her ödemesinden komisyon kazanırsınız (çok kademeli).
          </p>
        </div>

        {/* multi-level tier structure */}
        <div className="rounded-xl border p-3" style={{ borderColor: "var(--border-subtle)", background: "var(--bg-main)" }}>
          <div className="text-[10px] uppercase tracking-wider mb-2" style={{ color: "var(--text-muted)" }}>Komisyon Kademeleri</div>
          <div className="flex flex-wrap gap-2">
            {(data?.commission_levels || []).map((l) => (
              <div key={l.level} data-testid={`commission-level-${l.level}`} className="flex-1 min-w-[80px] text-center rounded-lg px-2 py-2 border" style={{ borderColor: "var(--border-subtle)", background: "var(--bg-card)" }}>
                <div className="text-[9px] uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>{l.level}. Seviye</div>
                <div className="font-mono-term text-base font-bold" style={{ color: "#10B981" }}>%{l.pct}</div>
              </div>
            ))}
          </div>
        </div>

        {/* stats */}
        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-xl border px-3 py-3 text-center" style={{ borderColor: "var(--border-subtle)", background: "var(--bg-main)" }}>
            <div className="flex items-center justify-center gap-1 text-[10px] uppercase tracking-wider mb-1" style={{ color: "var(--text-muted)" }}><Users className="w-3 h-3" /> Davet</div>
            <div data-testid="referred-count" className="font-mono-term text-xl font-bold">{data?.referred_count ?? 0}</div>
          </div>
          <div className="rounded-xl border px-3 py-3 text-center" style={{ borderColor: "var(--border-subtle)", background: "var(--bg-main)" }}>
            <div className="flex items-center justify-center gap-1 text-[10px] uppercase tracking-wider mb-1" style={{ color: "var(--text-muted)" }}><Banknote className="w-3 h-3" /> Toplam Kazanç</div>
            <div data-testid="total-earned" className="font-mono-term text-xl font-bold" style={{ color: "#10B981" }}>{data?.total_earned ?? 0}</div>
          </div>
          <div className="rounded-xl border px-3 py-3 text-center" style={{ borderColor: "var(--border-subtle)", background: "var(--bg-main)" }}>
            <div className="flex items-center justify-center gap-1 text-[10px] uppercase tracking-wider mb-1" style={{ color: "var(--text-muted)" }}><Wallet className="w-3 h-3" /> Çekilebilir</div>
            <div className="font-mono-term text-xl font-bold" style={{ color: "#10B981" }}>{balance}</div>
          </div>
        </div>

        {/* earnings breakdown by level */}
        <div className="rounded-xl border p-3" style={{ borderColor: "var(--border-subtle)", background: "var(--bg-main)" }}>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider mb-2" style={{ color: "var(--text-muted)" }}>
            <Layers className="w-3 h-3" /> Seviye Bazında Kazanç
          </div>
          <div className="space-y-1.5">
            {(data?.level_breakdown || []).map((b) => {
              const color = LEVEL_COLORS[(b.level - 1) % LEVEL_COLORS.length];
              const pct = (data?.commission_levels || []).find((l) => l.level === b.level)?.pct;
              return (
                <div key={b.level} data-testid={`level-earning-${b.level}`} className="flex items-center gap-2 text-sm">
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: `${color}22`, color }}>S{b.level}</span>
                  <span className="text-[12px]" style={{ color: "var(--text-muted)" }}>%{pct} · {b.count} ödeme</span>
                  <span className="ml-auto font-mono-term font-bold" style={{ color: b.total > 0 ? "#10B981" : "var(--text-muted)" }}>{b.total} USDT</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* 5-level referral tree */}
        <div className="rounded-xl border p-3" style={{ borderColor: "var(--border-subtle)", background: "var(--bg-main)" }}>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider mb-2" style={{ color: "var(--text-muted)" }}>
            <Network className="w-3 h-3" /> Davet Ağacı (5 Seviye)
          </div>
          {tree.length === 0 ? (
            <div className="flex items-center gap-2 text-sm py-2" style={{ color: "var(--text-muted)" }}>
              <ChevronRight className="w-4 h-4" /> Henüz davet ettiğiniz kimse yok. Linkinizi paylaşın!
            </div>
          ) : (
            <div data-testid="referral-tree" className="space-y-1.5">
              {flattenTree(tree).map((n) => {
                const color = LEVEL_COLORS[(n.level - 1) % LEVEL_COLORS.length];
                return (
                  <div key={n.id} data-testid={`tree-node-${n.username}`}
                    className="flex items-center gap-2 rounded-lg px-3 py-1.5 border"
                    style={{ borderColor: "var(--border-subtle)", background: "var(--bg-card)", marginLeft: (n.level - 1) * 16 }}>
                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ background: `${color}22`, color }}>S{n.level}</span>
                    <span className="text-[12px] font-semibold truncate">{n.full_name || n.username}</span>
                    <span className="text-[11px] font-mono-term ml-auto" style={{ color: "var(--text-muted)" }}>@{n.username}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* withdraw form */}
        <div className="rounded-xl border p-4" style={{ borderColor: "var(--border-subtle)", background: "var(--bg-main)" }}>
          <div className="text-sm font-bold mb-3 flex items-center gap-2"><Banknote className="w-4 h-4 text-emerald-400" /> Para Çekimi (USDT · BEP20)</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <input data-testid="withdraw-amount-input" type="number" min={minW} step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
              placeholder={`Tutar (min ${minW})`}
              className="rounded-lg px-3 py-2 text-sm font-mono-term outline-none border focus:border-emerald-500"
              style={{ background: "var(--bg-card)", borderColor: "var(--border-subtle)", color: "var(--text-primary)" }} />
            <input data-testid="withdraw-address-input" type="text" value={address} onChange={(e) => setAddress(e.target.value)}
              placeholder="USDT (BEP20) adresi · 0x..."
              className="sm:col-span-2 rounded-lg px-3 py-2 text-sm font-mono-term outline-none border focus:border-emerald-500"
              style={{ background: "var(--bg-card)", borderColor: "var(--border-subtle)", color: "var(--text-primary)" }} />
          </div>
          <button data-testid="withdraw-submit-button" onClick={submit}
            disabled={submitting || !amount || !address || parseFloat(amount) < minW || parseFloat(amount) > balance}
            className="mt-3 w-full inline-flex items-center justify-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-xl text-white transition-all hover:brightness-110 disabled:opacity-50"
            style={{ background: "linear-gradient(135deg,#10B981,#059669)" }}>
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Banknote className="w-4 h-4" />} Çekim Talebi Oluştur
          </button>
          <p className="text-[11px] mt-2" style={{ color: "var(--text-muted)" }}>
            Minimum {minW} USDT. Tutar talep anında bakiyenizden düşülür; admin onayından sonra adresinize gönderilir.
          </p>
        </div>

        {/* history */}
        {withdrawals.length > 0 && (
          <div className="overflow-x-auto">
            <div className="text-[10px] uppercase tracking-wider mb-2" style={{ color: "var(--text-muted)" }}>Çekim Geçmişi</div>
            <table className="w-full text-sm">
              <tbody>
                {withdrawals.map((w) => {
                  const m = STATUS_META[w.status] || STATUS_META.pending;
                  const Icon = m.icon;
                  return (
                    <tr key={w.id} data-testid={`withdrawal-row-${w.id}`} className="border-t" style={{ borderColor: "var(--border-subtle)" }}>
                      <td className="px-2 py-2 font-mono-term font-bold" style={{ color: "#10B981" }}>{w.amount} USDT</td>
                      <td className="px-2 py-2 font-mono-term text-[11px] truncate max-w-[160px]" style={{ color: "var(--text-muted)" }}>{w.address}</td>
                      <td className="px-2 py-2 text-right">
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full" style={{ background: m.bg, color: m.color }}>
                          <Icon className="w-3 h-3" /> {m.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
